<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import { applicantSchema } from '@/validation';
import { capitalise } from '@/helpers/format';
import type { ApplicantEntry, ApplicantTypeEnum } from '@/types/Applicant';
import { useLoanAppProcess } from '@/helpers/useLoanAppProcess';
import { Gender, genderOptions } from '@/helpers/const';
import { forceScrollToTop } from '@/helpers/forceScrollToTop';
import { noWhiteSpaceKeys } from '@/helpers/noWhiteSpaceKeys';
import { useRelationshipManager } from '@/helpers/useRelationshipManager';

const { loanApp } = useLoanAppProcess();
const props = withDefaults(
  defineProps<{
    type: ApplicantTypeEnum;
    modelValue: ApplicantEntry;
    primary?: ApplicantEntry;
    allApplicants?: ApplicantEntry[];
    currentId?: string;
  }>(),
  {
    primary: undefined,
    allApplicants: () => [],
    currentId: '',
  },
);
const emit = defineEmits<{
  (e: 'update:modelValue', value: ApplicantEntry): void;
  (e: 'relationship-change', applicantId: string, newStatus: string, oldStatus?: string): void;
}>();

const brokerId = loanApp.value.brokerId ?? '';
const schema = computed(() => applicantSchema(props.primary, props.allApplicants, props.currentId));

const { meta, submitCount, values, handleSubmit, resetForm, setFieldValue } = useForm<ApplicantEntry>({
  validationSchema: schema,
  validateOnMount: false,
  initialValues: { ...props.modelValue, type: props.type },
});

const applicantsRef = ref<ApplicantEntry[]>(props.allApplicants || []);

const { getRelationshipOptions, validate } = useRelationshipManager(applicantsRef);

const relationshipOptions = computed(() => {
  return getRelationshipOptions(props.currentId);
});

const relationshipErrors = computed(() => {
  const allErrors = validate.value;
  const id = props.currentId;
  if (!id) return [];
  return allErrors.filter((error) => error.includes(`[${id}]`));
});

const shouldShowRelationshipErrors = computed(() => {
  return relationshipErrors.value.length > 0 && (submitCount.value > 0 || meta.value.touched);
});

const update = handleSubmit(
  (values) => {
    if (values.relationshipStatus !== props.modelValue.relationshipStatus) {
      emit(
        'relationship-change',
        props.currentId,
        values.relationshipStatus || '',
        props.modelValue.relationshipStatus || '',
      );
    }

    emit('update:modelValue', values);
    forceScrollToTop(0);
    return true;
  },
  () => false,
);

const cancel = () => {
  resetForm();
  forceScrollToTop(0);
};

const updatePreferredName = () => {
  values.firstName = capitalise(values.firstName);
  if (values.firstName && !values.preferredName) {
    setFieldValue('preferredName', values.firstName);
  }
};

const onlyValidPhoneKeys = (event: KeyboardEvent) => {
  if ((event.key < '0' || event.key > '9') && event.key !== '+') {
    event.preventDefault();
  }
};

defineExpose({ update, cancel, resetForm, setFieldValue });

watch(
  () => props.modelValue,
  (applicant) => {
    resetForm({ values: { ...applicant, type: props.type } });
  },
  { immediate: true, deep: true },
);

watch(
  () => props.allApplicants,
  (newApplicants) => {
    applicantsRef.value = newApplicants || [];
  },
  { immediate: true, deep: true },
);

watch(
  () => applicantsRef.value,
  (newApplicants) => {
    const currentApplicant = newApplicants.find((a) => a.id === props.currentId);
    if (currentApplicant && currentApplicant.relationshipStatus !== values.relationshipStatus) {
      setFieldValue('relationshipStatus', currentApplicant.relationshipStatus);
    }
  },
  { deep: true },
);
</script>

<template>
  <form>
    <div>
      <div class="form-group">
        <BxField v-slot="{ field }" name="firstName">
          <BxInput
            :id="values.id + '-first-name'"
            class="form-control"
            v-bind="field"
            label="First name"
            label-type="floating"
            @blur="updatePreferredName()"
          />
        </BxField>
        <BxField v-slot="{ field }" name="middleName">
          <BxInput
            :id="values.id + '-middle-name'"
            class="form-control"
            v-bind="field"
            label="Middle name"
            label-type="floating"
            @blur="values.middleName = capitalise(values.middleName)"
          />
        </BxField>
        <BxField v-slot="{ field }" name="lastName">
          <BxInput
            :id="values.id + '-last-name'"
            class="form-control"
            v-bind="field"
            label="Last name"
            label-type="floating"
            @blur="values.lastName = capitalise(values.lastName)"
          />
        </BxField>
        <BxField v-slot="{ field }" name="preferredName">
          <BxInput
            :id="values.id + '-preferred-name'"
            class="form-control"
            v-bind="field"
            label="Preferred name"
            label-type="floating"
          />
        </BxField>
        <BxField v-slot="{ field }" name="relationshipStatus">
          <BxSelect
            :id="values.id + '-relationship-status'"
            class="form-control"
            v-bind="field"
            :list-items="relationshipOptions"
            label="Relationship"
            label-type="floating"
          />
        </BxField>
        <div v-if="shouldShowRelationshipErrors" class="mt-2 text-sm text-red-600">
          <p
            v-for="error in relationshipErrors.map((error) => error.replace(/^\[[^\]]+\]\s*/, ''))"
            :key="error"
            class="mb-1"
          >
            {{ error }}
          </p>
        </div>
      </div>
      <div class="form-control !mt-10">
        <BxField v-slot="{ field }" name="gender">
          <BxRadio
            :id="values.id + '-gender'"
            v-bind="field"
            :options="genderOptions"
            label="Gender"
            label-type="default"
            block
          />
        </BxField>
        <Transition name="fade">
          <div v-show="values.gender === Gender.OTHER">
            <BxField v-slot="{ field }" name="genderOther">
              <BxInput class="ml-7 mt-3" v-bind="field" label="" placeholder="Describe your gender identity" />
            </BxField>
          </div>
        </Transition>
      </div>
      <BxField v-slot="{ field }" name="birthdate">
        <BxDateInput
          :id="values.id + '-date-of-birth'"
          class="form-control !mt-10"
          v-bind="field"
          label="Your date of birth"
          label-type="floating"
          placeholder="DD/MM/YYYY"
        />
      </BxField>
      <BxField v-slot="{ field }" name="address">
        <BxAddressInput
          :id="values.id + '-address'"
          class="form-control"
          v-bind="field"
          label="Residential Address"
          label-type="floating"
        />
      </BxField>
      <div v-if="brokerId" class="applicant-note">
        <strong>Contact details</strong>
        <p>
          These details must belong to the applicant. <br />
          You will not be able to submit using your own details.
        </p>
      </div>
      <BxField v-slot="{ field }" name="mobile">
        <BxInput
          :id="values.id + '-mobile-phone-number'"
          class="form-control"
          inputmode="tel"
          v-bind="field"
          label="Mobile number"
          label-type="floating"
          minlength="10"
          :maxlength="values.mobile?.startsWith('+') ? 15 : 10"
          @keypress="onlyValidPhoneKeys"
        />
      </BxField>
      <BxField v-slot="{ field }" name="email">
        <BxInput
          :id="values.id + '-email'"
          class="form-control"
          inputmode="email"
          v-bind="field"
          label="Email address"
          label-type="floating"
          @keypress="noWhiteSpaceKeys"
        />
      </BxField>
      <BxField v-slot="{ field }" name="confirmEmail">
        <BxInput
          :id="values.id + '-confirm-email'"
          class="form-control"
          inputmode="email"
          v-bind="field"
          label="Confirm email address"
          label-type="floating"
          @keypress="noWhiteSpaceKeys"
        />
      </BxField>
    </div>
    <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
      Please review the error messages above.
    </BxErrorPanel>
  </form>
</template>

<style lang="scss" scoped>
.applicant-note {
  background: #f5f9fc;
  width: fit-content;
  padding: 0.6rem;
  border-radius: 0.3rem;
  margin: 1rem 0;

  strong,
  p {
    font-size: 1rem;
    line-height: 1.3rem;
  }

  strong {
    color: #040a34;
  }

  p {
    font-weight: 500;
    color: #02864e;
    font-size: 0.8rem;
    margin-top: 0.2rem;
  }
}
</style>
